import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, ApiError } from './api';
import { MeResponse, Role, UserDoc, WorkshopDoc } from './types';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'unauthorized'; message: string }
  | { status: 'error'; message: string }
  | { status: 'authenticated'; user: UserDoc; activeWorkshop: WorkshopDoc | null };

interface AuthContextValue {
  state: AuthState;
  refresh: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = async () => {
    setState({ status: 'loading' });
    // 1) Check SWA's auth state. /.auth/me always returns JSON, never 401.
    let clientPrincipal: unknown = null;
    try {
      const authRes = await fetch('/.auth/me', { credentials: 'include' });
      const data = await authRes.json();
      clientPrincipal = data?.clientPrincipal ?? null;
    } catch {
      // Network error — fall through to login redirect
    }
    if (!clientPrincipal) {
      window.location.href =
        '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
      return;
    }
    // 2) Signed in via AAD — now check authorization in our Users table.
    //    Retry transient failures: the SWA function host cold-starts (and is briefly
    //    unavailable during a deploy), which surfaced to users as a hard "Could not
    //    load app" on an otherwise healthy sign-in.
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const me = await api.get<MeResponse>('/api/current-user');
        setState({ status: 'authenticated', user: me.user, activeWorkshop: me.activeWorkshop });
        return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          const msg = (e.body as any)?.message ||
            'Your account is not authorized for Workshop Planner. Please contact mohanj@softura.com.';
          setState({ status: 'unauthorized', message: msg });
          return;
        }
        if (e instanceof ApiError && e.status === 401) {
          window.location.href =
            '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
          return;
        }
        // 5xx and non-JSON responses are usually transient — back off and retry.
        const retriable = !(e instanceof ApiError) || e.status >= 500 || e.status === 0;
        if (retriable && attempt < MAX_ATTEMPTS) {
          await new Promise(r => setTimeout(r, attempt * 1500));
          continue;
        }
        const errMsg = e instanceof ApiError
          ? `API error ${e.status}: ${(e.body as any)?.error || (e.body as any)?.message || e.message}`
          : `Network error: ${(e as Error)?.message || String(e)}`;
        setState({ status: 'error', message: errMsg });
        return;
      }
    }
  };

  useEffect(() => { refresh(); }, []);

  const hasRole = (...roles: Role[]) =>
    state.status === 'authenticated' && roles.includes(state.user.role);

  return <AuthContext.Provider value={{ state, refresh, hasRole }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
